// src/auth/authSession.ts
export type SessionStatus = "anon" | "needs-mfa" | "authed";

export type Session = {
  userId: string;
  status: SessionStatus;
  hotelId?: string | null;
};

const LS_SESSION = "xentros.session.v1";
const LS_JWT = "xentros.jwt.v1";

export const SESSION_EXPIRED_EVENT = "xentros-session-expired";

export function dispatchSessionExpired() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

export const PERMISSION_DENIED_EVENT = "xentros-permission-denied";

export function dispatchPermissionDenied(message?: string) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PERMISSION_DENIED_EVENT, { detail: { message } }));
  }
}

export function getSession(): Session | null {
  const raw = localStorage.getItem(LS_SESSION);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function setSession(session: Session) {
  localStorage.setItem(LS_SESSION, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(LS_SESSION);
  clearJwtToken();
}

// ─── JWT token storage ────────────────────────────────────────────────────────

export function getJwtToken(): string | null {
  return localStorage.getItem(LS_JWT);
}

export function setJwtToken(token: string) {
  localStorage.setItem(LS_JWT, token);
}

export function clearJwtToken() {
  localStorage.removeItem(LS_JWT);
}

export function isJwtTokenExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false;
    return Math.floor(Date.now() / 1000) >= payload.exp;
  } catch {
    return true;
  }
}

export function getValidJwtToken(): string | null {
  const token = getJwtToken();
  if (!token) return null;
  if (isJwtTokenExpired(token)) {
    clearJwtToken();
    return null;
  }
  return token;
}
